import 'swiper/css'

import styles from './index.module.css'

import React, { useCallback, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'

import mixpanel from '@disco/api/mixpanel'
import { getConstanceSetting } from '@disco/state-management/selectors/user'
import { sendDiscoverMixpanelEvent } from '@disco/utils/mixpanel/discover-page'

import type { PlaylistCurated } from 'types/playlist'

import Carousel from 'components/common/carousel'
import {
  CarouselSkeleton,
  CuratedPlaylistCardSkeleton,
} from 'components/common/carousel/carousel-skeleton'
import AspectRatio from 'components/ui/aspect-ratio'
import Button from 'components/ui/button'
import Stack from 'components/ui/stack'
import { Box } from 'components/ds'

import { useOptimizedImage } from 'hooks/use-optimized-image'
import { useSelector } from 'hooks/use-redux'

import CuratedPlaylistCard from '../../curated-playlists'

type Props = {
  curatedPlaylists: (PlaylistCurated | null | undefined)[]
  loading?: boolean
}

const CuratedPlaylistsSection = ({
  curatedPlaylists,
  loading = false,
}: Props): React.ReactNode => {
  const navigate = useNavigate()
  const artworkRef = useRef<HTMLDivElement | null>(null)
  const [isHovered, setIsHovered] = useState<number | null>(null)

  const validPlaylists = useMemo(
    () => curatedPlaylists.filter(Boolean) as PlaylistCurated[],
    [curatedPlaylists],
  )
  const curatedPlaylist = validPlaylists[0]

  // Feature flag for Discover Page Rebrand
  const discoverPageRebrand2026 = useSelector(state =>
    getConstanceSetting(state, 'DISCOVER-PAGE-REBRAND-2026'),
  )

  const handleVisitPlaylist = useCallback(
    (playlistIndex: number, playlist: PlaylistCurated) => {
      sendDiscoverMixpanelEvent('Click Image', {
        context: 'Discover Music Home',
        playlistId: playlist.id,
        playlist: playlist.name,
        playlistBusiness: playlist?.business?.business_name,
        playlistBusinessId: playlist?.business?.business_id,
      })

      // Pass playlist data via navigation state to avoid Redux lookups (too slow)
      navigate(`/discover/playlist?index=${playlistIndex}`, {
        state: { playlist },
      })
    },
    [navigate],
  )

  // Image optimization for legacy path (flag OFF)
  const optimizedImageUrl = useOptimizedImage({
    image: curatedPlaylist?.image,
    containerRef: artworkRef,
  })

  // Feature flag ON - use new Carousel with CuratedPlaylistCard
  if (discoverPageRebrand2026) {
    // Show skeleton while loading
    if (loading) {
      return (
        <CarouselSkeleton
          itemCount={3}
          ItemSkeleton={CuratedPlaylistCardSkeleton}
        />
      )
    }

    if (validPlaylists.length === 0) {
      return null
    }

    return (
      <Box mb="7">
        <Carousel
          navKey="curated-playlists"
          slidesPerView="auto"
          spaceBetween={16}
          useContainerBreakpoints
        >
          {validPlaylists.map((playlist, index) => (
            <CuratedPlaylistCard
              key={playlist.id || index}
              playlist={playlist}
              index={index}
              onHover={setIsHovered}
              isHovered={isHovered}
              onClick={() => handleVisitPlaylist(index, playlist)}
            />
          ))}
        </Carousel>
      </Box>
    )
  }

  if (validPlaylists.length === 0 && !loading) {
    return null
  }

  // Original behavior (flag OFF) - single playlist card
  return (
    <div className={styles.curatedPlaylistsSection}>
      <div className={styles.curatedPlaylistsSectionColumn}>
        <div
          className={styles.curatedPlaylistsSectionImageContainer}
          ref={artworkRef}
        >
          <AspectRatio image={optimizedImageUrl} ratio={1.55} shape="square" />
        </div>
      </div>
      <div className={styles.curatedPlaylistsSectionColumn}>
        <Stack>
          <h2 className={styles.curatedPlaylistsSectionTitle}>
            {curatedPlaylist?.name}
          </h2>
          <div className={styles.curatedPlaylistsSectionDescription}>
            {curatedPlaylist?.description}
          </div>
          <Button
            appearance="primary"
            link={curatedPlaylist?.view_playlist_url}
            linkTarget="_blank"
            caption={curatedPlaylist?.button_text || 'Visit playlist'}
            onClick={() => {
              mixpanel.sendPureEvent(
                'Open curated playlist from Browse Libraries',
              )
            }}
          />
        </Stack>
      </div>
    </div>
  )
}

export default CuratedPlaylistsSection
